/**
 * MealPlanningPage.jsx
 * Individual meal planning page with package selection and menu item selection
 * Design: Step-by-step, calm, guided experience
 * Enhanced with dietary notes and special requests
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Check,
  ChevronRight,
  Loader2,
  Users,
  Calendar,
  UtensilsCrossed,
  Leaf,
  Drumstick,
  Sparkles,
  AlertCircle,
  CheckCircle2,
  MessageSquare,
  Heart,
  AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import CustomerLayout from "./CustomerLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/customer`;

// Meal type labels
const MEAL_LABELS = {
  breakfast: "Breakfast",
  lunch: "Lunch",
  high_tea: "High Tea",
  dinner: "Dinner",
  welcome_drink: "Welcome Drink"
};

// Updated category icons
const CATEGORY_ICONS = {
  soup_veg: "🍲",
  soup_nonveg: "🍲",
  salad_veg: "🥗",
  salad_nonveg: "🥗",
  appetizer_veg: "🥬",
  appetizer_veg_quick: "🥟",
  appetizer_nonveg: "🍗",
  gravy_veg: "🍛",
  gravy_nonveg: "🍖",
  starch_veg: "🍚",
  starch_nonveg: "🍚",
  breads: "🫓",
  accompaniments: "🥒",
  desserts: "🍰",
  live_counter: "👨‍🍳"
};

// Dietary restriction options
const DIETARY_OPTIONS = [
  { id: "jain", label: "Jain (No onion, garlic, root vegetables)" },
  { id: "no_onion_garlic", label: "No Onion/Garlic" },
  { id: "gluten_free", label: "Gluten Free" },
  { id: "nut_allergy", label: "Nut Allergy" },
  { id: "dairy_free", label: "Dairy Free" },
  { id: "vegan", label: "Vegan" },
  { id: "low_spice", label: "Low Spice/Mild" },
  { id: "diabetic_friendly", label: "Diabetic Friendly" }
];

const MealPlanningPage = () => {
  const navigate = useNavigate();
  const { day, mealType } = useParams();
  const dayNum = parseInt(day);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(1); // 1: Package, 2: Menu Selection, 3: Review
  
  const [packages, setPackages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [mealPlan, setMealPlan] = useState(null);
  
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selections, setSelections] = useState({}); // {category_id: [item_ids]}
  const [activeCategory, setActiveCategory] = useState(null);
  const [guestCount, setGuestCount] = useState(100);
  
  // Dietary notes state
  const [dietaryNotes, setDietaryNotes] = useState({
    special_requests: "",
    dietary_restrictions: [],
    allergies: "",
    guest_notes: ""
  });
  const [showDietarySection, setShowDietarySection] = useState(false);

  // Get stored data
  const customer = JSON.parse(localStorage.getItem("customer_data") || "{}");
  const wedding = JSON.parse(localStorage.getItem("wedding_data") || "{}");

  // Fetch all required data
  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem("customer_token");
      if (!token) {
        navigate("/my-wedding");
        return;
      }

      try {
        const headers = { Authorization: `Bearer ${token}` };
        
        const [packagesRes, categoriesRes, itemsRes, mealRes] = await Promise.all([
          axios.get(`${API}/food/packages`, { headers }),
          axios.get(`${API}/food/categories`, { headers }),
          axios.get(`${API}/food/items`, { headers }),
          axios.get(`${API}/food/meals/${dayNum}/${mealType}`, { headers })
        ]);

        setPackages(packagesRes.data.packages);
        setCategories(categoriesRes.data.categories);
        setMenuItems(itemsRes.data.items);
        setMealPlan(mealRes.data);
        setGuestCount(mealRes.data.guest_count || 100);
        
        // Load dietary notes if exists
        if (mealRes.data.dietary_notes) {
          setDietaryNotes(mealRes.data.dietary_notes);
        }

        // If meal plan exists with package, set state
        if (mealRes.data.package_name) {
          const pkg = packagesRes.data.packages.find(p => p.name === mealRes.data.package_name);
          setSelectedPackage(pkg);
          
          // Load existing selections
          const existingSelections = {};
          (mealRes.data.selections || []).forEach(sel => {
            existingSelections[sel.category] = sel.items || [];
          });
          setSelections(existingSelections);
          
          // Go to step 2 if package selected
          setStep(2);
        }
      } catch (error) {
        if (error.response?.status === 401) {
          localStorage.removeItem("customer_token");
          navigate("/my-wedding");
        } else {
          toast.error("Unable to load data");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate, dayNum, mealType]);

  // Select package
  const handleSelectPackage = async (pkg) => {
    const token = localStorage.getItem("customer_token");
    setSaving(true);

    try {
      await axios.post(
        `${API}/food/meals/${dayNum}/${mealType}/package?package_name=${encodeURIComponent(pkg.name)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSelectedPackage(pkg);
      setSelections({});
      setStep(2);
      toast.success(`${pkg.name} package selected`);
    } catch (error) {
      toast.error("Failed to select package");
    } finally {
      setSaving(false);
    }
  };

  // Toggle item selection
  const handleToggleItem = (categoryKey, itemId) => {
    const packageCategory = selectedPackage?.categories.find(c => c.category === categoryKey);
    const allowed = packageCategory?.allowed || 0;
    const currentSelections = selections[categoryKey] || [];

    if (currentSelections.includes(itemId)) {
      // Remove item
      setSelections(prev => ({
        ...prev,
        [categoryKey]: prev[categoryKey].filter(id => id !== itemId)
      }));
    } else {
      // Add item if within limit
      if (currentSelections.length < allowed) {
        setSelections(prev => ({
          ...prev,
          [categoryKey]: [...(prev[categoryKey] || []), itemId]
        }));
      } else {
        toast.info(`You can select up to ${allowed} items in this category`);
      }
    }
  };

  // Save selections
  const handleSaveSelections = async () => {
    const token = localStorage.getItem("customer_token");
    setSaving(true);

    try {
      const selectionsArray = Object.entries(selections).map(([category, items]) => ({
        category_id: category,
        item_ids: items
      }));

      await axios.post(
        `${API}/food/meals/${dayNum}/${mealType}/selections`,
        selectionsArray,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Also save dietary notes
      await axios.post(
        `${API}/food/meals/${dayNum}/${mealType}/dietary-notes`,
        dietaryNotes,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Selections and preferences saved");
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to save selections";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };
  
  // Toggle dietary restriction
  const toggleDietaryRestriction = (restrictionId) => {
    setDietaryNotes(prev => ({
      ...prev,
      dietary_restrictions: prev.dietary_restrictions.includes(restrictionId)
        ? prev.dietary_restrictions.filter(r => r !== restrictionId)
        : [...prev.dietary_restrictions, restrictionId]
    }));
  };

  // Submit meal plan
  const handleSubmit = async () => {
    const token = localStorage.getItem("customer_token");
    setSaving(true);

    try {
      // First save selections
      const selectionsArray = Object.entries(selections).map(([category, items]) => ({
        category_id: category,
        item_ids: items
      }));

      await axios.post(
        `${API}/food/meals/${dayNum}/${mealType}/selections`,
        selectionsArray,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Save dietary notes
      await axios.post(
        `${API}/food/meals/${dayNum}/${mealType}/dietary-notes`,
        dietaryNotes,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Then submit
      const response = await axios.post(
        `${API}/food/meals/${dayNum}/${mealType}/submit`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      setStep(3);
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to submit menu";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // Calculate progress
  const getProgress = useCallback(() => {
    if (!selectedPackage) return { completed: 0, total: 0, percent: 0, missing: [] };

    const requiredCategories = selectedPackage.categories.filter(c => c.allowed > 0);
    let completed = 0;
    const missing = [];

    requiredCategories.forEach(cat => {
      const selected = selections[cat.category] || [];
      if (selected.length >= cat.allowed) {
        completed++;
      } else if (selected.length < cat.allowed) {
        const categoryInfo = categories.find(c => c.key === cat.category);
        missing.push(categoryInfo?.name || cat.category);
      }
    });

    return {
      completed,
      total: requiredCategories.length,
      percent: requiredCategories.length > 0 ? Math.round((completed / requiredCategories.length) * 100) : 0,
      missing
    };
  }, [selectedPackage, selections, categories]);

  const progress = getProgress();

  if (loading) {
    return (
      <CustomerLayout customer={customer} wedding={wedding}>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-10 h-10 animate-spin text-[#C9A87C] mx-auto mb-4" />
            <p className="text-[#8B7355]">Loading menu options...</p>
          </div>
        </div>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout customer={customer} wedding={wedding}>
      <div className="space-y-6">
        {/* Back Button & Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/my-wedding/food-menu")}
            className="p-2 rounded-full hover:bg-[#F5EDE8] transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-[#5C4A3D]" />
          </button>
          <div>
            <h1 className="text-xl sm:text-2xl font-serif text-[#5C4A3D]">
              Day {dayNum} • {MEAL_LABELS[mealType] || mealType}
            </h1>
            <p className="text-sm text-[#8B7355] flex items-center gap-2">
              <Users className="w-4 h-4" />
              {guestCount} Guests
            </p>
          </div>
        </div>

        {/* Step 1: Package Selection */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="text-center py-4">
              <h2 className="text-lg font-medium text-[#5C4A3D] mb-2">
                Select Your Food Package
              </h2>
              <p className="text-sm text-[#8B7355]">
                Choose a package that suits your celebration style
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {packages.map((pkg) => (
                <button
                  key={pkg.name}
                  onClick={() => handleSelectPackage(pkg)}
                  disabled={saving}
                  className="group bg-white/70 backdrop-blur-sm rounded-2xl p-6 border-2 border-[#E8DED5] hover:border-[#C9A87C] transition-all duration-300 text-left"
                  data-testid={`package-${pkg.name.toLowerCase()}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-lg font-semibold text-[#5C4A3D] group-hover:text-[#8B6914]">
                      {pkg.name}
                    </h3>
                    <span className="px-2 py-1 bg-[#C9A87C]/10 rounded-full text-xs font-medium text-[#8B6914]">
                      {pkg.total_dishes} dishes
                    </span>
                  </div>
                  <p className="text-sm text-[#8B7355] mb-4">
                    {pkg.description}
                  </p>
                  <div className="flex items-center gap-1 text-[#C9A87C] text-sm font-medium">
                    <span>Select Package</span>
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Menu Selection */}
        {step === 2 && selectedPackage && (
          <div className="space-y-6">
            {/* Selected Package Badge */}
            <div className="flex flex-wrap items-center justify-between gap-4 bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-[#E8DED5]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C9A87C] to-[#B08968] flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-[#8B7355]">Selected Package</p>
                  <p className="font-semibold text-[#5C4A3D]">{selectedPackage.name}</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStep(1)}
                className="border-[#C9A87C] text-[#8B6914]"
              >
                Change Package
              </Button>
            </div>

            {/* Progress */}
            <div className="bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-[#E8DED5]">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-[#5C4A3D]">Menu Progress</p>
                <p className="text-sm text-[#C9A87C]">{progress.completed} of {progress.total} categories</p>
              </div>
              <Progress value={progress.percent} className="h-2 bg-[#F5EDE8]" />
              {progress.missing.length > 0 && (
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Remaining: {progress.missing.slice(0, 3).join(", ")}
                  {progress.missing.length > 3 && ` +${progress.missing.length - 3} more`}
                </p>
              )}
            </div>

            {/* Category Tabs */}
            <div className="overflow-x-auto pb-2">
              <div className="flex gap-2 min-w-max">
                {selectedPackage.categories.filter(c => c.allowed > 0).map((cat) => {
                  const categoryInfo = categories.find(c => c.key === cat.category);
                  const selected = (selections[cat.category] || []).length;
                  const isComplete = selected >= cat.allowed;

                  return (
                    <button
                      key={cat.category}
                      onClick={() => setActiveCategory(cat.category)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        activeCategory === cat.category
                          ? "bg-[#C9A87C] text-white"
                          : isComplete
                          ? "bg-green-50 text-green-700 border border-green-200"
                          : "bg-white border border-[#E8DED5] text-[#5C4A3D] hover:border-[#C9A87C]"
                      }`}
                    >
                      <span>{CATEGORY_ICONS[cat.category] || "🍽️"}</span>
                      <span>{categoryInfo?.name || cat.category}</span>
                      <span className={`text-xs ${activeCategory === cat.category ? "text-white/80" : isComplete ? "text-green-600" : "text-[#8B7355]"}`}>
                        {selected}/{cat.allowed}
                      </span>
                      {isComplete && activeCategory !== cat.category && (
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Menu Items for Active Category */}
            {activeCategory && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-[#5C4A3D]">
                    {categories.find(c => c.key === activeCategory)?.name}
                  </h3>
                  <p className="text-sm text-[#8B7355]">
                    Select {selectedPackage.categories.find(c => c.category === activeCategory)?.allowed || 0} items
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {menuItems
                    .filter(item => item.category === activeCategory)
                    .map((item) => {
                      const isSelected = (selections[activeCategory] || []).includes(item.id);

                      return (
                        <button
                          key={item.id}
                          onClick={() => handleToggleItem(activeCategory, item.id)}
                          className={`flex items-start gap-3 p-4 rounded-xl border-2 transition-all text-left ${
                            isSelected
                              ? "border-[#C9A87C] bg-[#C9A87C]/5"
                              : "border-[#E8DED5] bg-white/70 hover:border-[#C9A87C]/50"
                          }`}
                          data-testid={`item-${item.id}`}
                        >
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            isSelected
                              ? "border-[#C9A87C] bg-[#C9A87C]"
                              : "border-[#D4C5B0]"
                          }`}>
                            {isSelected && <Check className="w-4 h-4 text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-[#5C4A3D] truncate">{item.name}</p>
                              {item.is_veg ? (
                                <Leaf className="w-4 h-4 text-green-600 flex-shrink-0" />
                              ) : (
                                <Drumstick className="w-4 h-4 text-red-500 flex-shrink-0" />
                              )}
                            </div>
                            {item.description && (
                              <p className="text-xs text-[#8B7355] mt-1 line-clamp-2">{item.description}</p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                </div>
              </div>
            )}

            {/* No active category selected */}
            {!activeCategory && (
              <div className="text-center py-12 bg-white/50 rounded-2xl border border-dashed border-[#D4C5B0]">
                <UtensilsCrossed className="w-12 h-12 text-[#C9A87C]/50 mx-auto mb-4" />
                <p className="text-[#8B7355]">Select a category above to start choosing dishes</p>
              </div>
            )}

            {/* Dietary Notes & Special Requests Section */}
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-[#E8DED5] overflow-hidden">
              <button
                onClick={() => setShowDietarySection(!showDietarySection)}
                className="w-full flex items-center justify-between p-4 hover:bg-[#FAF5F2] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#E8B4B8] to-[#D4A5A5] flex items-center justify-center">
                    <Heart className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-medium text-[#5C4A3D]">Dietary Preferences & Special Requests</h3>
                    <p className="text-xs text-[#8B7355]">
                      {dietaryNotes.dietary_restrictions.length > 0 || dietaryNotes.special_requests
                        ? `${dietaryNotes.dietary_restrictions.length} restrictions selected`
                        : "Add Jain, allergies, or special notes for your guests"}
                    </p>
                  </div>
                </div>
                <ChevronRight className={`w-5 h-5 text-[#8B7355] transition-transform ${showDietarySection ? "rotate-90" : ""}`} />
              </button>

              {showDietarySection && (
                <div className="p-4 pt-0 space-y-5 border-t border-[#E8DED5]">
                  {/* Dietary Restrictions */}
                  <div className="space-y-3 pt-4">
                    <Label className="text-[#5C4A3D] font-medium flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      Dietary Restrictions
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {DIETARY_OPTIONS.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => toggleDietaryRestriction(option.id)}
                          className={`px-3 py-2 rounded-full text-sm font-medium transition-all ${
                            dietaryNotes.dietary_restrictions.includes(option.id)
                              ? "bg-[#C9A87C] text-white"
                              : "bg-[#FAF5F2] text-[#5C4A3D] hover:bg-[#F5EDE8] border border-[#E8DED5]"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Allergies */}
                  <div className="space-y-2">
                    <Label className="text-[#5C4A3D] font-medium">Allergies (if any)</Label>
                    <Textarea
                      value={dietaryNotes.allergies}
                      onChange={(e) => setDietaryNotes(prev => ({ ...prev, allergies: e.target.value }))}
                      placeholder="E.g., Peanut allergy for 5 guests, Shellfish allergy for bride's mother..."
                      className="bg-[#FAF5F2] border-[#E8DED5] focus:border-[#C9A87C] min-h-[80px]"
                    />
                  </div>

                  {/* Special Requests */}
                  <div className="space-y-2">
                    <Label className="text-[#5C4A3D] font-medium flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-[#C9A87C]" />
                      Special Requests
                    </Label>
                    <Textarea
                      value={dietaryNotes.special_requests}
                      onChange={(e) => setDietaryNotes(prev => ({ ...prev, special_requests: e.target.value }))}
                      placeholder="E.g., Please prepare a separate Jain meal counter, Low salt options for elderly guests..."
                      className="bg-[#FAF5F2] border-[#E8DED5] focus:border-[#C9A87C] min-h-[80px]"
                    />
                  </div>

                  {/* Guest Notes */}
                  <div className="space-y-2">
                    <Label className="text-[#5C4A3D] font-medium">Additional Notes for Our Chef</Label>
                    <Textarea
                      value={dietaryNotes.guest_notes}
                      onChange={(e) => setDietaryNotes(prev => ({ ...prev, guest_notes: e.target.value }))}
                      placeholder="Any other preferences or requests for this meal..."
                      className="bg-[#FAF5F2] border-[#E8DED5] focus:border-[#C9A87C] min-h-[60px]"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                variant="outline"
                onClick={handleSaveSelections}
                disabled={saving}
                className="flex-1 border-[#C9A87C] text-[#8B6914]"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Progress
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={saving || progress.percent < 100}
                className="flex-1 bg-gradient-to-r from-[#C9A87C] to-[#B08968] hover:from-[#B8976B] hover:to-[#9F7857]"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {progress.percent < 100 ? "Complete All Categories" : "Review & Submit Menu"}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-serif text-[#5C4A3D] mb-3">
              Menu Submitted Successfully!
            </h2>
            <p className="text-[#8B7355] max-w-md mx-auto mb-6">
              Your {MEAL_LABELS[mealType]} menu has been submitted. Our culinary team will review it and confirm shortly.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="outline"
                onClick={() => {
                  setStep(2);
                  // Reload data
                  window.location.reload();
                }}
                className="border-[#C9A87C] text-[#8B6914]"
              >
                Edit Menu
              </Button>
              <Button
                onClick={() => navigate("/my-wedding/food-menu")}
                className="bg-gradient-to-r from-[#C9A87C] to-[#B08968]"
              >
                Back to All Meals
              </Button>
            </div>
          </div>
        )}
      </div>
    </CustomerLayout>
  );
};

export default MealPlanningPage;
